import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type CurrentUser, type RequestDetail, type ReturnStatus } from '../api';
import { Alert, Badge, Button, Card, Switch } from './ui';
import { TextArea, TextField } from './form';
import { SignatureDialog } from './SignatureDialog';

const SECTION_TITLE: Record<string, string> = {
  HARDWARE: 'Hardware',
  SYSTEM: 'Software & systems',
  REMOTE_ACCESS: 'Remote access',
  SHAREPOINT_SITE: 'SharePoint sites',
  FILESHARE_FOLDER: 'Fileshare folders',
};

/**
 * What IT does with an approved request: work through the checklist, record the
 * technician and the mailbox actually created, then sign off.
 */
export function ImplementationPanel({ request, user }: { request: RequestDetail; user: CurrentUser }) {
  const queryClient = useQueryClient();
  const mine = request.admin?.id === user.id;

  const [done, setDone] = useState<Set<string>>(
    () => new Set(request.items.filter((item) => item.implementedAt).map((item) => item.catalogItemId)),
  );
  const [technicianName, setTechnicianName] = useState(request.implementation?.technicianName ?? '');
  const [createdEmail, setCreatedEmail] = useState(request.implementation?.createdEmailAddress ?? '');
  const [notes, setNotes] = useState(request.implementation?.implementationNotes ?? '');
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (patch: Parameters<typeof api.saveImplementation>[1]) => api.saveImplementation(request.id, patch),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  });

  const assign = useMutation({
    mutationFn: () => api.assignRequest(request.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['request', request.id] });
      void queryClient.invalidateQueries({ queryKey: ['it-queue'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not pick it up.'),
  });

  const complete = useMutation({
    mutationFn: (signature: string) => api.completeRequest(request.id, signature),
    onSuccess: () => {
      setSigning(false);
      void queryClient.invalidateQueries({ queryKey: ['request', request.id] });
      void queryClient.invalidateQueries({ queryKey: ['it-queue'] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not sign off.'),
  });

  // Tick-offs save straight away; the text fields save on a short debounce.
  useEffect(() => {
    if (request.status !== 'IN_PROGRESS' || !mine) return;
    const timer = setTimeout(() => {
      save.mutate({
        technicianName: technicianName.trim() || null,
        createdEmailAddress: createdEmail.trim() || null,
        implementationNotes: notes.trim() || null,
      });
    }, 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianName, createdEmail, notes]);

  const grouped = useMemo(() => {
    const map = new Map<string, RequestDetail['items']>();
    for (const item of request.items) {
      const list = map.get(item.category) ?? [];
      list.push(item);
      map.set(item.category, list);
    }
    return [...map.entries()];
  }, [request.items]);

  const offboarding = request.type === 'OFFBOARDING';

  // Returns are tracked locally so the selects respond immediately; the server
  // is the record, this is just the echo.
  const [returns, setReturns] = useState<Record<string, { status: ReturnStatus | null; condition: string }>>(() =>
    Object.fromEntries(
      request.items
        .filter((item) => item.category === 'HARDWARE')
        .map((item) => [
          item.catalogItemId,
          { status: item.returnStatus ?? null, condition: item.returnCondition ?? '' },
        ]),
    ),
  );

  const hardware = request.items.filter((item) => item.category === 'HARDWARE');
  const unaccounted = offboarding ? hardware.filter((item) => !returns[item.catalogItemId]?.status).length : 0;

  const outstanding = request.items.length - done.size;
  // Pushed to the onsite tech and not yet ticked done: the server refuses
  // completion too; this only stops the button promising otherwise.
  const onsiteOutstanding = request.onsite !== null && request.onsite.doneAt === null;
  const ready = outstanding === 0 && unaccounted === 0 && technicianName.trim().length > 0 && !onsiteOutstanding;

  function setReturn(catalogItemId: string, patch: { status?: ReturnStatus | null; condition?: string }) {
    const current = returns[catalogItemId] ?? { status: null, condition: '' };
    const next = { ...current, ...patch };
    setReturns({ ...returns, [catalogItemId]: next });
    save.mutate({
      returns: [{ catalogItemId, returnStatus: next.status, returnCondition: next.condition.trim() || null }],
    });
  }

  function toggle(catalogItemId: string, on: boolean) {
    const next = new Set(done);
    if (on) next.add(catalogItemId);
    else next.delete(catalogItemId);
    setDone(next);
    save.mutate({ doneItemIds: [...next] });
  }

  function setAll(on: boolean) {
    const next = on ? new Set(request.items.map((item) => item.catalogItemId)) : new Set<string>();
    setDone(next);
    save.mutate({ doneItemIds: [...next] });
  }

  if (request.status === 'APPROVED') {
    return (
      <Card className="mt-6 p-6">
        <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
          IT implementation
        </h2>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          This request is approved and waiting to be picked up. Taking it marks it as in progress so nobody else
          works the same one.
        </p>
        {error && (
          <div className="mt-4">
            <Alert>{error}</Alert>
          </div>
        )}
        <div className="mt-4">
          <Button onClick={() => assign.mutate()} disabled={assign.isPending}>
            {assign.isPending ? 'Picking up…' : 'Pick this up'}
          </Button>
        </div>
      </Card>
    );
  }

  if (!mine) {
    return (
      <Card className="mt-6 p-6">
        <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
          IT implementation
        </h2>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
          {request.admin?.displayName ?? 'Another administrator'} is working on this one.
        </p>
        <div className="mt-4">
          <Button variant="secondary" onClick={() => assign.mutate()} disabled={assign.isPending}>
            Take it over
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="mt-6 p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-xs font-semibold tracking-widest text-slate-500 uppercase dark:text-slate-400">
          IT implementation
        </h2>
        <Badge tone={outstanding === 0 ? 'success' : 'warning'} dot>
          {done.size} of {request.items.length} done
        </Badge>
        {offboarding && hardware.length > 0 && (
          <Badge tone={unaccounted === 0 ? 'success' : 'warning'}>
            {hardware.length - unaccounted} of {hardware.length} accounted for
          </Badge>
        )}
        <span className="ml-auto flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setAll(true)}>
            Tick all
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setAll(false)}>
            Clear all
          </Button>
        </span>
      </div>

      {error && (
        <div className="mt-4">
          <Alert>{error}</Alert>
        </div>
      )}

      <div className="mt-5 space-y-6">
        {grouped.map(([category, items]) => (
          <div key={category}>
            <h3 className="text-xs font-medium text-slate-500 dark:text-slate-400">
              {SECTION_TITLE[category] ?? category}
            </h3>
            <ul className="mt-2 grid gap-x-8 sm:grid-cols-2">
              {items
                .slice()
                .sort((a, b) => (a.legacyNumber ?? 0) - (b.legacyNumber ?? 0))
                .map((item) => {
                  const on = done.has(item.catalogItemId);
                  return (
                    <li key={item.catalogItemId} className="border-b border-slate-100 py-2 dark:border-slate-800">
                      <div className="flex items-center gap-3">
                      <Switch
                        id={`done-${item.catalogItemId}`}
                        checked={on}
                        onChange={(next) => toggle(item.catalogItemId, next)}
                        label={item.name}
                      />
                      {item.legacyNumber !== null && (
                        <span className="w-7 text-right text-xs tabular-nums text-slate-500 dark:text-slate-400">{item.legacyNumber}</span>
                      )}
                      <label
                        htmlFor={`done-${item.catalogItemId}`}
                        className={
                          on
                            ? 'min-w-0 flex-1 cursor-pointer truncate text-sm text-slate-500 line-through dark:text-slate-400'
                            : 'min-w-0 flex-1 cursor-pointer truncate text-sm text-slate-800 dark:text-slate-200'
                        }
                      >
                        {item.name}
                        {item.assetDetails && (
                          <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">{item.assetDetails}</span>
                        )}
                      </label>
                      {item.accessLevel && (
                        <Badge tone={item.accessLevel === 'RW' ? 'copper' : 'neutral'}>{item.accessLevel}</Badge>
                      )}
                      {item.revoke === false && <Badge tone="warning">Kept</Badge>}
                      </div>

                      {offboarding && item.category === 'HARDWARE' && (
                        <div className="mt-2 flex flex-wrap items-center gap-2 pl-11">
                          <select
                            aria-label={`Return status for ${item.name}`}
                            value={returns[item.catalogItemId]?.status ?? ''}
                            onChange={(event) =>
                              setReturn(item.catalogItemId, {
                                status: (event.target.value || null) as ReturnStatus | null,
                              })
                            }
                            className={`shrink-0 rounded-lg border px-2 py-1 text-xs ${
                              returns[item.catalogItemId]?.status
                                ? 'border-slate-300 bg-white text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300'
                                : 'border-copper-300 bg-copper-50 text-copper-800 dark:border-copper-800 dark:bg-copper-950/40 dark:text-copper-200'
                            }`}
                          >
                            <option value="">Not recorded</option>
                            <option value="RETURNED">Returned</option>
                            <option value="NOT_RETURNED">Not returned</option>
                            <option value="NOT_APPLICABLE">Not applicable</option>
                          </select>

                          <input
                            value={returns[item.catalogItemId]?.condition ?? ''}
                            onChange={(event) =>
                              setReturns({
                                ...returns,
                                [item.catalogItemId]: {
                                  status: returns[item.catalogItemId]?.status ?? null,
                                  condition: event.target.value,
                                },
                              })
                            }
                            onBlur={(event) => setReturn(item.catalogItemId, { condition: event.target.value })}
                            placeholder="Condition, damage, anything missing"
                            aria-label={`Condition of ${item.name}`}
                            className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                          />
                        </div>
                      )}
                    </li>
                  );
                })}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-5 border-t border-slate-200 pt-6 sm:grid-cols-2 dark:border-slate-800">
        <TextField
          label="Technician name"
          required
          value={technicianName}
          onChange={setTechnicianName}
          hint="Whoever did the work on site, as on the paper form."
          maxLength={160}
        />
        {!offboarding && (
          <TextField
            label="Email address created"
            type="email"
            value={createdEmail}
            onChange={setCreatedEmail}
            hint="The mailbox actually set up, if one was."
            maxLength={160}
          />
        )}
        <div className="sm:col-span-2">
          <TextArea
            label="Implementation notes"
            rows={4}
            value={notes}
            onChange={setNotes}
            maxLength={4000}
            placeholder="Anything worth recording: serials issued, deviations from the request, follow-ups needed."
          />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-6 dark:border-slate-800">
        <Button
          onClick={() => {
            setError(null);
            setSigning(true);
          }}
          disabled={!ready}
          title={ready ? undefined : onsiteOutstanding ? 'Tick the onsite work done first' : 'Tick everything off and record the technician first'}
        >
          Complete &amp; sign off
        </Button>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {ready
            ? 'Signing off closes the request and finishes the signed record.'
            : outstanding > 0
              ? `${outstanding} item${outstanding === 1 ? '' : 's'} still to tick off.`
              : onsiteOutstanding
                ? 'The onsite work has not been ticked done yet.'
                : "Record the technician's name first."}
        </p>
      </div>

      <SignatureDialog
        open={signing}
        title="Complete and sign off"
        attestation={`You are signing off ${request.reference} as implemented${
          technicianName ? `, with ${technicianName} as the technician` : ''
        }. This closes the request and completes the signed record.`}
        confirmLabel="Sign off"
        signerName={user.displayName}
        busy={complete.isPending}
        error={error}
        onCancel={() => setSigning(false)}
        onConfirm={(dataUri) => complete.mutate(dataUri)}
      />
    </Card>
  );
}
